import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from "react-aria-components";
import type { SelectProps, PopoverProps } from "react-aria-components";
import type { IcIcon } from "@jetblue/kit-icons/types";
import { ExpandableIndicator } from "../expandable-indicator";
import type { ReactNode } from "react";
import { cn } from "../../utilities/tailwind";

export type InputSelectOption<Value extends string> = {
  href?: string;
  label: string;
  value: Value;
};
export type InputSelectProperties<Value extends string> = {
  /**
   * Icon component to display in the input.
   */
  Icon?: IcIcon | undefined;
  /**
   * Classname applied to the html select element.
   */
  className?: string | undefined;
  /**
   * Classname applied to the Icon.
   */
  iconClassName?: string | undefined;
  /**
   * Error message or component to display when validation fails.
   */
  error?: ReactNode | undefined;
  /**
   * Whether the input is open.
   */
  isOpen?: boolean | undefined;
  /**
   * Label displayed for the input.
   */
  label: string;
  /**
   * Options available for selection.   Each option includes a `value`, a `label` and an optional `href`.
   */
  options: InputSelectOption<Value>[];
  /**
   * Properties for the popover component.
   */
  popoverProperties?: PopoverProps | undefined;
  /**
   * The current selected value of the input.
   */
  value: Value;
  /**
   * The style variant of the component. Defaults to `"simple"`.
   * - `"simple"`: Minimalist style for compact use cases.
   * - `"standard"`: Enhanced styling for forms with labels and borders.
   */
  variant?: "simple" | "standard";
} & Omit<
  SelectProps,
  "children" | "className" | "selectedKey" | "slot" | "style"
>;
const VARIANT_STYLES = {
  simple: {
    label: cn(
      "pointer-events-none absolute m-4 font-sans text-body text-on-surface-variant-medium transition-all peer-data-[selected=true]:mt-2 peer-data-[selected=true]:text-[12px] peer-data-[selected=true]:text-on-surface",
    ),
    trigger: cn(
      "group/expandable-ui flex items-center gap-2 font-sans text-s-body-extra-bold text-primary focus-outline outline-none disabled:opacity-50",
    ),
  },
  standard: {
    label: cn(
      "pointer-events-none absolute top-0 m-4 font-sans text-body text-on-surface-variant-medium transition-all peer-data-[selected=true]:mt-2 peer-data-[selected=true]:text-[12px] peer-data-[selected=true]:text-on-surface",
    ),
    trigger: cn(
      "group/expandable-ui peer relative flex h-14 w-full border border-outline bg-surface-container px-4 pe-5 pt-6 pb-2 font-sans text-body text-on-surface group-data-[invalid=true]/select:border-status-negative focus-within:outline-2 focus-within:outline-primary focus-within:group-data-[invalid=true]/select:border-status-negative disabled:bg-disabled disabled:opacity-50",
    ),
  },
};
const EXPANDABLE_INDICATOR_STYLES = {
  base: cn("text-primary"),
  simple: cn(""),
  standard: cn("absolute end-4 top-5 size-5"),
};
const POPOVER_STYLES = {
  base: cn(
    "flex w-(--trigger-width) min-w-32 flex-col overflow-hidden rounded-md border border-outline bg-surface-container shadow-jb-1",
  ),
  standard: cn("-mt-2 rounded-t-none"),
};
const LIST_BOX_ITEM_STYLES = {
  base: cn(
    "cursor-pointer px-4 py-3 text-start font-sans text-primary transition-all outline-none hover:bg-state-surface-container-hover-variant focus:bg-state-surface-container-hover-variant aria-selected:bg-primary aria-selected:text-on-primary",
  ),
  link: cn("block text-inherit no-underline"),
};

export const InputSelect = <Value extends string>({
  Icon,
  className,
  iconClassName,
  isOpen = false,
  label,
  onOpenChange,
  // eslint-disable-next-line @typescript-eslint/no-deprecated,sonarjs/deprecation -- will do soon
  onSelectionChange,
  options,
  error,
  popoverProperties,
  value,
  variant = "standard",
  ...properties
}: InputSelectProperties<Value>) => (
  <Select
    className={className}
    onOpenChange={onOpenChange}
    // eslint-disable-next-line @typescript-eslint/no-deprecated,sonarjs/deprecation -- will do soon
    onSelectionChange={onSelectionChange}
    placeholder={variant === "simple" ? label : ""}
    // eslint-disable-next-line @typescript-eslint/no-deprecated,sonarjs/deprecation -- will do soon
    selectedKey={value}
    {...properties}
  >
    <div
      className={cn("relative w-full", {
        "flex h-14 w-full": variant === "standard",
      })}
    >
      <Button
        aria-expanded={isOpen}
        className={VARIANT_STYLES[variant].trigger}
        data-selected={value ? "true" : "false"}
      >
        {Icon && <Icon aria-hidden className={iconClassName} />}

        <SelectValue />

        <ExpandableIndicator
          aria-hidden
          className={cn(
            EXPANDABLE_INDICATOR_STYLES.base,
            EXPANDABLE_INDICATOR_STYLES[variant],
          )}
        />
      </Button>

      {variant === "standard" && (
        <Label className={VARIANT_STYLES[variant].label}>{label}</Label>
      )}
    </div>

    <Popover
      className={cn(POPOVER_STYLES.base, {
        [POPOVER_STYLES.standard]: variant === "standard",
      })}
      {...popoverProperties}
    >
      <ListBox shouldFocusWrap>
        {options.map(({ href, label, value }) => (
          <ListBoxItem
            className={cn(LIST_BOX_ITEM_STYLES.base, {
              [LIST_BOX_ITEM_STYLES.link]: Boolean(href),
            })}
            href={href}
            id={value}
            key={value}
          >
            {label}
          </ListBoxItem>
        ))}
      </ListBox>
    </Popover>
    {error}
  </Select>
);
InputSelect.displayName = "InputSelect";

export { useSelect } from "./use-select";
